/**
 * Frontend JavaScript for the multilingual video block.
 */

interface VideoState {
	currentTime: number;
	paused: boolean;
	volume: number;
	muted: boolean;
}

class MultilingualVideo {
	private video: HTMLVideoElement;
	private buttons: NodeListOf< HTMLButtonElement >;
	private buttonParents: HTMLDivElement[] = [];
	private activeClass = '';
	private defaultClass = '';
	private currentState: VideoState = {
		currentTime: 0,
		paused: true,
		volume: 1,
		muted: false,
	};
	private placeholderFrame: HTMLCanvasElement | null = null;

	constructor( container: HTMLElement ) {
		this.video = container.querySelector(
			'.wp-block-phrase-com-multilingual-video__video'
		) as HTMLVideoElement;
		this.buttons = container.querySelectorAll(
			'.wp-block-phrase-com-multilingual-video__language-button'
		) as NodeListOf< HTMLButtonElement >;

		if ( ! this.video || ! this.buttons.length ) {
			return;
		}

		this.init();
	}

	private init(): void {
		this.buttons.forEach( ( button ) => {
			// Add click event listeners to language buttons.
			button.addEventListener( 'click', ( event ) => {
				event.preventDefault();
				this.switchLanguage( button );
			} );

			// Init button parents.
			const parent = button.closest( '.wp-block-button' ) as HTMLDivElement | null;
			if ( parent ) {
				this.buttonParents.push( parent );
				if ( button.classList.contains( 'is-active' ) && ! this.activeClass ) {
					this.activeClass = parent.className;
				}
				if ( ! button.classList.contains( 'is-active' ) && ! this.defaultClass ) {
					this.defaultClass = parent.className;
				}
			}
		} );
		this.video.addEventListener( 'play', () => {
			this.currentState.paused = false;
		} );
		this.video.addEventListener( 'pause', () => {
			this.currentState.paused = true;
		} );
		this.video.addEventListener( 'volumechange', () => {
			this.currentState.volume = this.video.volume;
			this.currentState.muted = this.video.muted;
		} );
	}

	/**
	 * Overlay the current video frame as a canvas placeholder.
	 */
	private setPlaceholderFrame(): HTMLCanvasElement | null {
		if ( this.video.readyState < 2 ) {
			return null;
		}
		const canvas = document.createElement( 'canvas' );
		canvas.width = this.video.videoWidth;
		canvas.height = this.video.videoHeight;
		const ctx = canvas.getContext( '2d' );
		if ( ! ctx ) {
			return null;
		}
		ctx.drawImage( this.video, 0, 0, canvas.width, canvas.height );
		return canvas;
	}

	private switchLanguage( button: HTMLButtonElement ): void {
		const newLanguage = button.dataset.language;
		const newVideoUrl = button.dataset.videoUrl;
		if ( ! newLanguage || ! newVideoUrl || newVideoUrl === this.video.src ) {
			return;
		}

		// Store current video state
		this.saveCurrentState();

		// Use placeholder of last frame and freeze height.
		this.placeholderFrame = this.setPlaceholderFrame();
		if ( this.placeholderFrame ) {
			this.video.before( this.placeholderFrame );
		}
		this.video.style.height = `${ this.video.getBoundingClientRect().height }px`;

		// Update active button.
		this.buttons.forEach( ( btn ) => btn.classList.remove( 'is-active' ) );
		button.classList.add( 'is-active' );
		this.buttonParents.forEach( ( parent ) => {
			parent.className = parent.contains( button ) ? this.activeClass : this.defaultClass;
		} );

		// Create a new video element to preload and switch smoothly.
		const newVideo = this.video.cloneNode() as HTMLVideoElement;
		newVideo.src = newVideoUrl;
		newVideo.load();

		newVideo.addEventListener(
			'loadedmetadata',
			() => {
				// Set the new source and restore state.
				this.video.src = newVideoUrl;
				this.video.load();

				// Wait for the video to be ready then restore state.
				this.video.addEventListener(
					'loadedmetadata',
					() => {
						this.restoreVideoState();
					},
					{ once: true }
				);

				// Remove placeholder after playback time is restored.
				this.video.addEventListener(
					'seeked',
					() => {
						this.removePlaceholderFrame();
						this.video.style.height = '';
					},
					{ once: true }
				);
			},
			{ once: true }
		);

		// Fallback in case preloading fails.
		setTimeout( () => {
			this.removePlaceholderFrame();
			this.video.style.height = '';
		}, 2000 );
	}

	private removePlaceholderFrame(): void {
		if ( this.placeholderFrame ) {
			this.placeholderFrame.remove();
			this.placeholderFrame = null;
		}
	}

	private saveCurrentState(): void {
		this.currentState = {
			currentTime: this.video.currentTime,
			paused: this.video.paused,
			volume: this.video.volume,
			muted: this.video.muted,
		};
	}

	private restoreVideoState(): void {
		// Restore volume settings first.
		this.video.volume = this.currentState.volume;
		this.video.muted = this.currentState.muted;

		const onLoaded = () => {
			// Restore playback position.
			if ( this.video.duration && this.currentState.currentTime <= this.video.duration ) {
				this.video.currentTime = this.currentState.currentTime;
			}

			// Restore playback state.
			if ( ! this.currentState.paused ) {
				this.video.play().catch( ( error ) => {
					// eslint-disable-next-line no-console -- Intentional diagnostic for autoplay failures.
					console.warn( 'Could not auto-resume video playback:', error );
				} );
			}
		};
		this.video.addEventListener( 'loadeddata', onLoaded, { once: true } );
	}
}

// Initialize all multilingual video blocks on the page
document.addEventListener( 'DOMContentLoaded', () => {
	const videoBlocks = document.querySelectorAll( '.wp-block-phrase-com-multilingual-video' );
	videoBlocks.forEach( ( block ) => {
		new MultilingualVideo( block as HTMLElement );
	} );
} );
